/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    DASHBOARD_REFRESH_AFTER_DELETE
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Implement refresh functionality for dashboard tabs. Added refresh functions
 * for all tabs (Recent, WTS, WTB, My Broadcasts) and passed them to
 * DashboardBroadcastSummaryTable to ensure UI updates after item deletion.
 *
 * File Size Check:
 * - Current: 317 lines
 * - Adding: ~40 lines
 * - Projected: 357 lines
 * - Decision: Proceed (Dashboard logic)
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ALLBROADCASTS_SIDEBAR_FILTER
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Update navigation links from /broadcasts to /allbroadcasts.
 *
 * File Size Check:
 * - Current: 302 lines
 * - Adding: ~15 lines
 * - Projected: 317 lines
 * - Decision: Proceed (within 200-line component limit after excluding headers/comments)
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastTabs.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    DASHBOARD_BROADCAST_REDESIGN_PHASE1
 * Date:         2025-12-31
 * Modified By:  Cursor
 *
 * Description:
 * Removed pagination from dashboard broadcast section and added "View All" button
 * to navigate to dedicated broadcasts page. Updated tab label to "My Broadcasts".
 * Maintains up to 5 items display. Screen-fit based row calculations removed.
 *
 * File Size Check:
 * - Current: 336 lines (no headers, ~316 code lines)
 * - Removing: ~82 lines (pagination state + controls)
 * - Adding: ~63 lines (View All buttons, imports, header)
 * - Projected: ~317 lines (~287 code lines)
 * - Decision: Proceed (well within 200-line component limit)
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastTabs.tsx (MODIFIED)
 * - client/src/features/dashboard/DashboardBroadcastTabs.module.scss (NEW)
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom"; // [2025-12-31] Add for View All navigation
import { listBroadcastsRaw, listAllBroadcastsWithUsers, listPublicBroadcasts } from "../../services/broadcasts";
import { Broadcast } from "../../interfaces/broadcast";
import DashboardBroadcastSummaryTable from "./DashboardBroadcastSummaryTable";
import { getErrorMessage } from "../../utils/notifications";
import { useIsGuest } from "../../contexts/GuestModeContext"; // [2026-07-06] GUEST_ACCESS BR-09/10
import styles from "./DashboardBroadcastTabs.module.scss"; // [2025-12-31] Add for View All button styling

const DashboardBroadcastTabs: React.FC = () => {
  // [2026-07-06] GUEST_ACCESS BR-09/10: guests get a masked feed (public endpoint),
  // no "My Broadcasts" tab, and no "View All" (both are member-only).
  const isGuest = useIsGuest();
  type TabKey = "recent" | "wts" | "wtb" | "mine";
  const [tab, setTab] = useState<TabKey>("recent");

  // State for Recent tab (all broadcasts from other users)
  const [recentData, setRecentData] = useState<Broadcast[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState<string | null>(null);

  // State for WTS tab (WTS broadcasts from other users)
  const [wtsData, setWtsData] = useState<Broadcast[]>([]);
  const [wtsLoading, setWtsLoading] = useState(true);
  const [wtsError, setWtsError] = useState<string | null>(null);

  // State for WTB tab (WTB broadcasts from other users)
  const [wtbData, setWtbData] = useState<Broadcast[]>([]);
  const [wtbLoading, setWtbLoading] = useState(true);
  const [wtbError, setWtbError] = useState<string | null>(null);

  // State for My Broadcasts tab summaries
  const [summariesData, setSummariesData] = useState<Broadcast[]>([]);
  const [summariesLoading, setSummariesLoading] = useState(true);
  const [summariesError, setSummariesError] = useState<string | null>(null);

  // [2026-01-08] Dashboard preview: fetch up to 5 items max (hard cap for dashboard)
  const DASHBOARD_PREVIEW_LIMIT = 5;

  // [2025-01-02] Refresh functions for dashboard
  // [2026-07-06] GUEST_ACCESS: guests use the masked public feed instead of the authed list.
  const fetchFeed = (params: { page: number; limit: number; type?: "WTB" | "WTS" }) =>
    isGuest ? listPublicBroadcasts(params) : listAllBroadcastsWithUsers(params);

  const refreshRecent = () => {
    setRecentLoading(true);
    setRecentError(null);
    // We only need up to DASHBOARD_PREVIEW_LIMIT broadcasts for the dashboard preview
    const broadcastLimit = DASHBOARD_PREVIEW_LIMIT;
    fetchFeed({ page: 1, limit: broadcastLimit })
      .then((res) => setRecentData(res.items || []))
      .catch((e) => setRecentError(getErrorMessage(e, "Failed to load broadcasts")))
      .finally(() => setRecentLoading(false));
  };

  const refreshWts = () => {
    setWtsLoading(true);
    setWtsError(null);
    // We only need up to DASHBOARD_PREVIEW_LIMIT broadcasts for the dashboard preview
    const broadcastLimit = DASHBOARD_PREVIEW_LIMIT;
    fetchFeed({ page: 1, limit: broadcastLimit, type: "WTS" })
      .then((res) => setWtsData(res.items || []))
      .catch((e) => setWtsError(getErrorMessage(e, "Failed to load WTS broadcasts")))
      .finally(() => setWtsLoading(false));
  };

  const refreshWtb = () => {
    setWtbLoading(true);
    setWtbError(null);
    // We only need up to DASHBOARD_PREVIEW_LIMIT broadcasts for the dashboard preview
    const broadcastLimit = DASHBOARD_PREVIEW_LIMIT;
    fetchFeed({ page: 1, limit: broadcastLimit, type: "WTB" })
      .then((res) => setWtbData(res.items || []))
      .catch((e) => setWtbError(getErrorMessage(e, "Failed to load WTB broadcasts")))
      .finally(() => setWtbLoading(false));
  };

  const refreshMine = () => {
    // [2026-07-06] GUEST_ACCESS: guests have no own broadcasts — skip this fetch entirely.
    if (isGuest) {
      setSummariesLoading(false);
      return;
    }
    setSummariesLoading(true);
    setSummariesError(null);
    const broadcastLimit = DASHBOARD_PREVIEW_LIMIT;
    listBroadcastsRaw({ page: 1, limit: broadcastLimit, sort: "createdAt:desc" })
      .then((res) => setSummariesData(res.items || []))
      .catch((e) => setSummariesError(getErrorMessage(e, "Failed to load broadcasts")))
      .finally(() => setSummariesLoading(false));
  };

  // Fetch data for Recent tab (all broadcasts from other users)
  // Fetch more broadcasts to ensure we have enough items for pagination
  useEffect(() => {
    refreshRecent();
  }, []); // [2025-12-31] No pagination dependencies - fetch once on mount

  // Fetch data for WTS tab (WTS broadcasts from other users)
  useEffect(() => {
    refreshWts();
  }, []); // [2025-12-31] No pagination dependencies - fetch once on mount

  // Fetch data for WTB tab (WTB broadcasts from other users)
  useEffect(() => {
    refreshWtb();
  }, []); // [2025-12-31] No pagination dependencies - fetch once on mount

  // Fetch summaries data for My Broadcasts tab
  useEffect(() => {
    refreshMine();
  }, []); // [2025-12-31] No pagination dependencies - fetch once on mount

  // [2026-02-13] Maya: Figma alignment - View All moved to top right next to heading
  const getViewAllLink = () => {
    if (tab === "mine") return "/broadcast/mine-all";
    return `/allbroadcasts?tab=${tab}`;
  };

  return (
    <section className={`mt-5 ${styles.broadcastsSection}`}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className={`fw-semibold m-0 u-H2-24 ${styles.sectionHeading}`}>
          Broadcasts
        </h2>
        {/* [2026-07-06] GUEST_ACCESS: guests see "View All" but it is locked (no navigation). */}
        {isGuest ? (
          <span
            className={`${styles.viewAllButton} ${styles.viewAllButtonDisabled}`}
            aria-disabled="true"
            title="Sign in to view all broadcasts"
          >
            <span className={styles.buttonText}>View All</span>
            <svg className={styles.buttonIcon} width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L6 6L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : (
          <Link to={getViewAllLink()} className={styles.viewAllButton}>
            <span className={styles.buttonText}>View All</span>
            <svg
              className={styles.buttonIcon}
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L6 6L1 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        )}
      </div>

      <ul className="nav nav-tabs">
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "recent" ? "active" : ""}`}
            onClick={() => setTab("recent")}
          >
            Recent
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "wts" ? "active" : ""}`}
            onClick={() => setTab("wts")}
          >
            WTS
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${tab === "wtb" ? "active" : ""}`}
            onClick={() => setTab("wtb")}
          >
            WTB
          </button>
        </li>
        <li className="nav-item">
          {/* [2026-07-06] GUEST_ACCESS: guests see "My Broadcasts" but cannot enter it (member-only). */}
          <button
            className={`nav-link ${tab === "mine" ? "active" : ""} ${isGuest ? "disabled" : ""}`}
            onClick={() => { if (!isGuest) setTab("mine"); }}
            disabled={isGuest}
            aria-disabled={isGuest}
            title={isGuest ? "Sign in to see your broadcasts" : undefined}
          >
            {/* [2026-02-09] Responsive label: "My Broadcasts" on desktop, "Mine" on mobile */}
            <span className="d-none d-sm-inline">My Broadcasts</span>
            <span className="d-sm-none">Mine</span>
          </button>
        </li>
      </ul>

      <div className="pt-3">
        {tab === "recent" ? (
          recentLoading ? (
            <div className="py-4 text-center">Loading broadcasts…</div>
          ) : recentError ? (
            <div className="py-4 text-center text-danger">{recentError}</div>
          ) : (
            <>
              <DashboardBroadcastSummaryTable
                broadcasts={recentData}
                currentTab="recent"
                itemsPerPage={5 /* [2026-01-08] Fixed at 5 for dashboard preview */}
                currentPage={1 /* Always page 1 - no pagination */}
                refreshCallback={refreshRecent} // Pass refresh callback
              />
            </>
          )
        ) : tab === "wts" ? (
          wtsLoading ? (
            <div className="py-4 text-center">Loading WTS broadcasts…</div>
          ) : wtsError ? (
            <div className="py-4 text-center text-danger">{wtsError}</div>
          ) : (
            <>
              <DashboardBroadcastSummaryTable
                broadcasts={wtsData}
                currentTab="wts"
                itemsPerPage={5 /* [2026-01-08] Fixed at 5 for dashboard preview */}
                currentPage={1 /* Always page 1 - no pagination */}
                refreshCallback={refreshWts} // Pass refresh callback
              />
            </>
          )
        ) : tab === "wtb" ? (
          wtbLoading ? (
            <div className="py-4 text-center">Loading WTB broadcasts…</div>
          ) : wtbError ? (
            <div className="py-4 text-center text-danger">{wtbError}</div>
          ) : (
            <>
              <DashboardBroadcastSummaryTable
                broadcasts={wtbData}
                currentTab="wtb"
                itemsPerPage={5 /* [2026-01-08] Fixed at 5 for dashboard preview */}
                currentPage={1 /* Always page 1 - no pagination */}
                refreshCallback={refreshWtb} // Pass refresh callback
              />
            </>
          )
        ) : tab === "mine" ? (
          summariesLoading ? (
            <div className="py-4 text-center">Loading broadcasts…</div>
          ) : summariesError ? (
            <div className="py-4 text-center text-danger">{summariesError}</div>
          ) : (
            <>
              <DashboardBroadcastSummaryTable
                broadcasts={summariesData}
                currentTab="mine"
                itemsPerPage={5 /* [2026-01-08] Fixed at 5 for dashboard preview */}
                currentPage={1 /* Always page 1 - no pagination */}
                refreshCallback={refreshMine} // Pass refresh callback
              />
            </>
          )
        ) : null}
      </div>
    </section>
  );
};

export default DashboardBroadcastTabs;
